/**
 * Canonical channel permission strings, copied verbatim from
 * openapi/generated/route_policy.gen.go. Controls are gated on the exact
 * permission the route demands, so no rendered button can 403.
 */
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ShieldX } from "lucide-react";
import { Card, CardContent } from "@erp/ui";

import { ModuleBoundary } from "@/features/apps/module-boundary";
import { useSession } from "@/lib/session/use-session";

export const CHANNEL_PERMISSIONS = {
  channelRead: "channels.channel.read",
  sourceSetRead: "channels.source_set.read",
  sourceSetManage: "channels.source_set.manage",
  orderRead: "channels.order.read",
  /** Purpose-specific: decrypting a retained payload is audited either way. */
  orderRawRead: "channels.order.raw.read",
  orderRetry: "channels.order.retry",
  reconciliationRead: "channels.reconciliation.read",
} as const;

export type ChannelPermission = (typeof CHANNEL_PERMISSIONS)[keyof typeof CHANNEL_PERMISSIONS];

/** Direct-route authorization: denied pages never mount or issue queries. */
export function ChannelPermissionBoundary({
  permission,
  children,
}: {
  permission: ChannelPermission;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { hasPermission, hasModule } = useSession();

  // The app comes before the grant, and in that order deliberately. If the
  // company has not installed this app, saying "you lack permission" sends
  // somebody to an administrator who cannot help — the fix is to install it,
  // not to be granted something. The server answers the same way round.
  if (!hasModule("channels")) return <ModuleBoundary app="channels">{children}</ModuleBoundary>;

  if (hasPermission(permission)) return <>{children}</>;

  return (
    <div className="flex min-h-[22rem] items-center justify-center p-4">
      <Card
        role="alert"
        aria-labelledby="channels-forbidden-title"
        className="w-full max-w-xl border-destructive/30 bg-card/95 shadow-sm"
        data-testid="channels-route-forbidden"
      >
        <CardContent className="flex gap-4 p-6 sm:p-8">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive">
            <ShieldX className="size-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 space-y-2">
            <h1 id="channels-forbidden-title" className="text-xl font-semibold tracking-tight">
              {t("channels.permissions.title")}
            </h1>
            <p className="text-sm text-muted-foreground">{t("errors.forbidden", { permission })}</p>
            <code dir="ltr" className="block w-fit rounded border bg-muted px-2 py-1 text-xs">
              {permission}
            </code>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
